/* eslint-disable no-case-declarations */
/* eslint-disable no-useless-escape */
import { Build } from '@stencil/core';
import {
    ChatbotAnswerInterface,
    ChatbotConfigurationInterface,
    ChatbotDialogOptionInterface,
    ChatbotEscalationInterface,
    ChatbotFaqList,
    ChatbotLinkInterface,
    ChatbotVideoInterface,
    ChatbotTextInterface,
    ChatbotEscalationFeedbackInterface,
    ChatbotHistoryInterface
} from './interfaces';
import { ChatbotFeedbackInterface } from './interfaces/chatbot-feedback.interface';
import {
    mapFaqAnswerRequest,
    mapFaqListRequest,
    mapFaqListResponse,
    mapFeedbackRequest,
    mapQuestionRequest,
    mapQuestionResponse
} from './mappers';
import { ApiChatbotRequestInterface } from './interfaces/api';
import { mapFeedbackResponse } from './mappers/feedback-response.mapper';
import { dataLayer } from '..';
import { ChatbotEscalationLivechatResponse } from './interfaces/escalation/chatbot-escalation-livechat.interface';

const errorMessage = 'Er is iets misgegaan';
const livechatStatusService = 'en-gb/cloudcrm-livechat/request/availability';

const fromObject = (o: object) =>
    Object.entries(o).reduce((search, [name, value], index) => `${index ? `${search}&` : '?'}${name}=${value}`, '?');

export class ChatBotService {
    private static instance: ChatBotService | null = null;
    private readonly configuration: Promise<ChatbotConfigurationInterface>;
    private readonly regexMap: { readonly [key: string]: RegExp } = {
        email: /(@|\b[\w\.-]+@[\w\.-]+\.\w{2,4}\b)/g,
        bankAccount:
            /\b(?:NL|BE)\d{2}\s*(?:[A-Z]{4}\s*)?(?:\d{10}|\d{4}\s*\d{4}\s*\d{4}|\d{2}\s*[A-Z]{4}\s*\d{4}\s*\d{2}|[A-Z]{4}\s*\d{1}\s*\d{4}\s*\d{5}|\d{2}\s*[A-Z]{4}\s*\d{4}\s*\d{4}\s*\d{2})\b|\b(?:NL|BE)\d{2}\s*[A-Z]{4}\s*\d{4}\s*\d{4}\s*\d{2}\b/gi,
        allNumbers: /\d+/g
    };

    private chatbotHistory: ChatbotHistoryInterface[] = [];

    private constructor(configuration: ChatbotConfigurationInterface = {} as ChatbotConfigurationInterface) {
        this.configuration = Build.isBrowser
            ? fetch('/static/api/chat.json').then(
                  async (response) => (await response.json()) as ChatbotConfigurationInterface
              )
            : Promise.resolve(configuration);
    }

    public static getInstance(configuration?: ChatbotConfigurationInterface): ChatBotService {
        if (!ChatBotService.instance) {
            ChatBotService.instance = new ChatBotService(configuration);
        }
        return ChatBotService.instance;
    }

    public static reset(): void {
        ChatBotService.instance = null;
    }

    getEscalation = (): Promise<ChatbotEscalationInterface> =>
        Build.isBrowser
            ? this.configuration.then(({ escalation }) => escalation)
            : Promise.resolve({} as ChatbotEscalationInterface);

    getTexts = (): Promise<ChatbotTextInterface> =>
        Build.isBrowser ? this.configuration.then(({ text }) => text) : Promise.resolve({} as ChatbotTextInterface);

    anonymizeText = (text: string): string =>
        text
            .replace(this.regexMap['email'], '*****@*****')
            .replace(this.regexMap['bankAccount'], (match) => match.replace(/\S/g, '*'))
            .replace(this.regexMap['allNumbers'], (match) => match.replace(/\S/g, '*'));

    mapToText = (answer: ChatbotAnswerInterface): string =>
        answer.elements
            .map(({ type, parts }): string => {
                let prefix = '';
                switch (type) {
                    case 'orderedList':
                        prefix = 'x. ';
                        break;
                    case 'unorderedList':
                        prefix = '- ';
                        break;
                    case 'paragraph':
                        prefix = '';
                        break;
                }
                return parts
                    .map(({ type, data }, index: number): string => {
                        switch (type) {
                            case 'text':
                                return (prefix.replace('x', (index + 1).toString()) + data) as string;
                            case 'link':
                                const link: ChatbotLinkInterface = data as ChatbotLinkInterface;
                                return `(link: ${link.text} - ${link.url})`;
                            case 'video':
                                const video: ChatbotVideoInterface = data as ChatbotVideoInterface;
                                return `(video: ${video.name} - ${video.videoId})`;
                            case 'dialogoption':
                                const dialogoption: ChatbotDialogOptionInterface = data as ChatbotDialogOptionInterface;
                                return `(dialogoption: ${dialogoption.text} - ${dialogoption.path})`;
                        }
                    })
                    .join(prefix ? '\r\n' : '');
            })
            .join('\r\n');

    askQuestion = (
        question: string,
        sessionId?: string,
        dialogPath?: string
    ): Promise<ChatbotAnswerInterface | string> => {
        try {
            return Build.isBrowser
                ? this.configuration.then((configuration) => {
                      const url = `${configuration.baseUrl}/ask`;
                      const request: Partial<ApiChatbotRequestInterface> = mapQuestionRequest(
                          configuration,
                          encodeURIComponent(question),
                          sessionId,
                          dialogPath
                      );
                      const response = fetch(`${url}${fromObject(request)}`).then(async (response) => {
                          const questionResponse = mapQuestionResponse(await response.json());

                          dataLayer.addToDataLayer({
                              event: 'chatbot',
                              vraag: this.anonymizeText(question),
                              answer: this.mapToText(questionResponse)
                          });

                          this.setChatbotHistory({
                              question: `${question} ${dialogPath ? '(dialog path: ' + dialogPath + ')' : ''}`,
                              answer: this.mapToText(questionResponse)
                          });

                          return questionResponse;
                      });

                      return response;
                  })
                : Promise.resolve(errorMessage);
        } catch (e) {
            return Promise.resolve(errorMessage);
        }
    };

    public getFaqList = (
        sessionId: string | undefined,
        classificationId: number,
        classificationName: string,
        step: number
    ): Promise<ChatbotFaqList> =>
        this.configuration.then((configuration) => {
            const url = `${configuration.baseUrl}/event/Escalation_${classificationName}_Funnelstep${step}`;
            const request: Partial<ApiChatbotRequestInterface> = mapFaqListRequest(
                configuration,
                classificationId,
                sessionId
            );
            return fetch(`${url}${fromObject(request)}`).then(async (response) =>
                mapFaqListResponse(await response.json())
            );
        });

    public getFaqAnswer = (faqId: number, sessionId?: string): Promise<ChatbotAnswerInterface> =>
        this.configuration.then((configuration) => {
            const url = `${configuration.baseUrl}/faqs/${faqId}/output`;
            const request: Partial<ApiChatbotRequestInterface> = mapFaqAnswerRequest(configuration, sessionId);
            return fetch(`${url}${fromObject(request)}`).then(async (response) =>
                mapQuestionResponse(await response.json())
            );
        });

    public sendFeedback = (
        sessionId: string,
        interactionId: string,
        { rating, comment, q }: ChatbotEscalationFeedbackInterface
    ): Promise<ChatbotFeedbackInterface> =>
        this.configuration.then((configuration) => {
            const url = `${configuration.baseUrl}/feedback`;
            const request: Partial<ApiChatbotRequestInterface> = mapFeedbackRequest(
                configuration,
                sessionId,
                interactionId,
                rating,
                encodeURIComponent(comment),
                encodeURIComponent(q)
            );
            return fetch(`${url}${fromObject(request)}`).then(async (response) =>
                mapFeedbackResponse(await response.json())
            );
        });

    private setChatbotHistory = (history: ChatbotHistoryInterface): void => {
        this.chatbotHistory = [...this.chatbotHistory, history];
    };

    public getChatbotHistory = (): ChatbotHistoryInterface[] => this.chatbotHistory;

    public isLivechatAvailable = async (): Promise<boolean> => {
        const { apiBaseUrl } = await this.configuration;

        return Build.isBrowser
            ? fetch(`${apiBaseUrl}/${livechatStatusService}`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json; charset=UTF-8' },
                  body: JSON.stringify({})
              })
                  .then(async (response) =>
                      this.mapLivechatResponse((await response.json()) as ChatbotEscalationLivechatResponse)
                  )
                  .catch(() => {
                      console.error('Error fetching livechat availability');
                      return false;
                  })
            : Promise.resolve(false);
    };

    private mapLivechatResponse = ({ result }: ChatbotEscalationLivechatResponse): boolean =>
        result.is_available && result.available_agents > 0;
}
